"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.prepareTransaction = void 0;
const cryptoassets_1 = require("@ledgerhq/cryptoassets");
const errors_1 = require("@ledgerhq/errors");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const account_1 = require("../../account");
const web3_1 = require("./api/chain/web3");
const errors_2 = require("./errors");
const logic_1 = require("./logic");
const tx_fees_1 = require("./tx-fees");
const utils_1 = require("./utils");
const jsHelpers_1 = require("@ledgerhq/coin-framework/bridge/jsHelpers");
function deriveCommandDescriptor(mainAccount, tx, api) {
    return __awaiter(this, void 0, void 0, function* () {
        const { model } = tx;
        switch (model.kind) {
            case "transfer":
                return deriveTransferCommandDescriptor(mainAccount, tx, model, api);
            case "token.transfer":
                return deriveTokenTransferCommandDescriptor(mainAccount, tx, model, api);
            case "token.createATA":
                return deriveCreateAssociatedTokenAccountCommandDescriptor(mainAccount, model, api);
            case "stake.createAccount":
                return deriveStakeCreateAccountCommandDescriptor(mainAccount, tx, model, api);
            case "stake.delegate":
                return deriveStakeDelegateCommandDescriptor(mainAccount, model, api);
            case "stake.undelegate":
                return deriveStakeUndelegateCommandDescriptor(mainAccount, model, api);
            case "stake.withdraw":
                return deriveStakeWithdrawCommandDescriptor(mainAccount, tx, model, api);
            case "stake.split":
                return deriveStakeSplitCommandDescriptor(mainAccount, tx, model, api);
            default:
                return (0, utils_1.assertUnreachable)(model);
        }
    });
}
const prepareTransaction = (mainAccount, tx, api) => __awaiter(void 0, void 0, void 0, function* () {
    const txToDeriveFrom = updateModelIfSubAccountIdPresent(tx);
    const commandDescriptor = yield deriveCommandDescriptor(mainAccount, txToDeriveFrom, api);
    const model = Object.assign(Object.assign({}, tx.model), { commandDescriptor });
    const patch = {
        model,
    };
    return (0, jsHelpers_1.defaultUpdateTransaction)(tx, patch);
});
exports.prepareTransaction = prepareTransaction;
const deriveTokenTransferCommandDescriptor = (mainAccount, tx, model, api) => __awaiter(void 0, void 0, void 0, function* () {
    const errors = {};
    const warnings = {};
    const subAccount = (0, account_1.findSubAccountById)(mainAccount, model.uiState.subAccountId);
    if (!subAccount || subAccount.type !== "TokenAccount") {
        throw new Error("subaccount not found");
    }
    yield validateRecipientCommon(mainAccount, tx, errors, warnings, api);
    const memo = model.uiState.memo;
    if (typeof memo === "string" && memo.length > 0) {
        validateMemoCommon(memo, errors);
    }
    const tokenIdParts = subAccount.token.id.split("/");
    const mintAddress = tokenIdParts[tokenIdParts.length - 1];
    const mintDecimals = subAccount.token.units[0].magnitude;
    const senderAssociatedTokenAccountAddress = (0, logic_1.decodeAccountIdWithTokenAccountAddress)(subAccount.id).address;
    if (!errors.recipient && tx.recipient === senderAssociatedTokenAccountAddress) {
        errors.recipient = new errors_2.SolanaTokenRecipientIsSenderATA();
    }
    const defaultRecipientDescriptor = {
        shouldCreateAsAssociatedTokenAccount: false,
        tokenAccAddress: "",
        walletAddress: "",
    };
    const recipientDescriptorOrError = errors.recipient
        ? defaultRecipientDescriptor
        : yield getTokenRecipient(tx.recipient, mintAddress, api);
    if (!errors.recipient && recipientDescriptorOrError instanceof Error) {
        errors.recipient = recipientDescriptorOrError;
    }
    const recipientDescriptor = recipientDescriptorOrError instanceof Error
        ? defaultRecipientDescriptor
        : recipientDescriptorOrError;
    const txFee = yield (0, tx_fees_1.estimateTxFee)(api, mainAccount, "token.transfer");
    const assocAccRentExempt = recipientDescriptor.shouldCreateAsAssociatedTokenAccount
        ? yield api.getAssocTokenAccMinNativeBalance()
        : 0;
    if (recipientDescriptor.shouldCreateAsAssociatedTokenAccount) {
        warnings.recipient = new errors_2.SolanaRecipientAssociatedTokenAccountWillBeFunded();
    }
    if (!tx.useAllAmount && tx.amount.lte(0)) {
        errors.amount = new errors_1.AmountRequired();
    }
    const txAmount = tx.useAllAmount ? subAccount.spendableBalance.toNumber() : tx.amount.toNumber();
    if (!errors.amount && txAmount > subAccount.spendableBalance.toNumber()) {
        errors.amount = new errors_1.NotEnoughBalance();
    }
    return {
        command: {
            kind: "token.transfer",
            ownerAddress: mainAccount.freshAddress,
            ownerAssociatedTokenAccountAddress: senderAssociatedTokenAccountAddress,
            amount: txAmount,
            mintAddress,
            mintDecimals,
            recipientDescriptor: recipientDescriptor,
            memo: model.uiState.memo,
        },
        fee: txFee + assocAccRentExempt,
        warnings,
        errors,
    };
});
function getTokenRecipient(recipientAddress, mintAddress, api) {
    return __awaiter(this, void 0, void 0, function* () {
        const recipientTokenAccount = yield (0, web3_1.getMaybeTokenAccount)(recipientAddress, api);
        if (recipientTokenAccount instanceof Error) {
            throw recipientTokenAccount;
        }
        if (recipientTokenAccount === undefined) {
            if (!(0, logic_1.isEd25519Address)(recipientAddress)) {
                return new errors_2.SolanaAddressOffEd25519();
            }
            const recipientAssociatedTokenAccountAddress = yield api.findAssocTokenAccAddress(recipientAddress, mintAddress);
            const shouldCreateAsAssociatedTokenAccount = !(yield isAccountFunded(recipientAssociatedTokenAccountAddress, api));
            return {
                walletAddress: recipientAddress,
                shouldCreateAsAssociatedTokenAccount,
                tokenAccAddress: recipientAssociatedTokenAccountAddress,
            };
        }
        else {
            if (recipientTokenAccount.mint.toBase58() !== mintAddress) {
                return new errors_2.SolanaTokenAccountHoldsAnotherToken();
            }
            if (recipientTokenAccount.state !== "initialized") {
                return new errors_2.SolanaTokenAccounNotInitialized();
            }
        }
        return {
            walletAddress: recipientTokenAccount.owner.toBase58(),
            shouldCreateAsAssociatedTokenAccount: false,
            tokenAccAddress: recipientAddress,
        };
    });
}
function deriveCreateAssociatedTokenAccountCommandDescriptor(mainAccount, model, api) {
    return __awaiter(this, void 0, void 0, function* () {
        const token = (0, cryptoassets_1.getTokenById)(model.uiState.tokenId);
        const tokenIdParts = token.id.split("/");
        const mint = tokenIdParts[tokenIdParts.length - 1];
        const associatedTokenAccountAddress = yield api.findAssocTokenAccAddress(mainAccount.freshAddress, mint);
        const txFee = yield (0, tx_fees_1.estimateTxFee)(api, mainAccount, "token.createATA");
        const assocAccRentExempt = yield api.getAssocTokenAccMinNativeBalance();
        return {
            fee: txFee + assocAccRentExempt,
            command: {
                kind: model.kind,
                mint: mint,
                owner: mainAccount.freshAddress,
                associatedTokenAccountAddress,
            },
            warnings: {},
            errors: {},
        };
    });
}
function deriveTransferCommandDescriptor(mainAccount, tx, model, api) {
    return __awaiter(this, void 0, void 0, function* () {
        const errors = {};
        const warnings = {};
        yield validateRecipientCommon(mainAccount, tx, errors, warnings, api);
        const memo = model.uiState.memo;
        if (typeof memo === "string" && memo.length > 0) {
            validateMemoCommon(memo, errors);
        }
        const fee = yield (0, tx_fees_1.estimateTxFee)(api, mainAccount, "transfer");
        const rentExemptMin = yield (0, web3_1.getAccountMinimumBalanceForRentExemption)(api, mainAccount.freshAddress);
        const txAmount = tx.useAllAmount
            ? bignumber_js_1.default.max(mainAccount.spendableBalance.minus(fee).minus(rentExemptMin), 0)
            : tx.amount;
        if (tx.useAllAmount) {
            if (txAmount.eq(0)) {
                errors.amount = new errors_1.NotEnoughBalance();
            }
        }
        else {
            if (txAmount.lte(0)) {
                errors.amount = new errors_1.AmountRequired();
            }
            else if (txAmount.plus(fee).plus(rentExemptMin).gt(mainAccount.spendableBalance)) {
                errors.amount = new errors_1.NotEnoughBalance();
            }
        }
        const command = {
            kind: "transfer",
            amount: txAmount.toNumber(),
            sender: mainAccount.freshAddress,
            recipient: tx.recipient,
            memo: model.uiState.memo,
        };
        return {
            command,
            fee,
            warnings,
            errors,
        };
    });
}
function deriveStakeCreateAccountCommandDescriptor(mainAccount, tx, model, api) {
    return __awaiter(this, void 0, void 0, function* () {
        const errors = {};
        const warnings = {};
        if (!tx.useAllAmount && tx.amount.lte(0)) {
            errors.amount = new errors_1.AmountRequired();
        }
        const txFee = yield (0, tx_fees_1.estimateTxFee)(api, mainAccount, "stake.createAccount");
        const stakeAccRentExemptAmount = yield (0, web3_1.getStakeAccountMinimumBalanceForRentExemption)(api);
        const rentExemptMin = yield (0, web3_1.getAccountMinimumBalanceForRentExemption)(api, mainAccount.freshAddress);
        const fee = txFee + stakeAccRentExemptAmount;
        const amount = tx.useAllAmount
            ? bignumber_js_1.default.max(mainAccount.spendableBalance.minus(fee).minus(rentExemptMin), 0)
            : tx.amount;
        if (!errors.amount && mainAccount.spendableBalance.lt(amount.plus(fee).plus(rentExemptMin))) {
            errors.amount = new errors_1.NotEnoughBalance();
        }
        const { uiState: { delegate }, } = model;
        yield validateValidatorCommon(delegate.voteAccAddress, errors, api);
        const stakeAccAddressSeed = `stake:${Math.random().toString()}`;
        const stakeAccAddress = yield (0, web3_1.getStakeAccountAddressWithSeed)({
            fromAddress: mainAccount.freshAddress,
            seed: stakeAccAddressSeed,
        });
        return {
            command: {
                kind: "stake.createAccount",
                amount: amount.toNumber(),
                stakeAccRentExemptAmount,
                fromAccAddress: mainAccount.freshAddress,
                stakeAccAddress,
                delegate,
                seed: stakeAccAddressSeed,
            },
            fee,
            warnings,
            errors,
        };
    });
}
function deriveStakeDelegateCommandDescriptor(mainAccount, model, api) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const errors = {};
        const { uiState } = model;
        const stake = validateAndTryGetStakeAccount(mainAccount, uiState.stakeAccAddr, errors);
        if (stake !== undefined && !stake.hasStakeAuth && !stake.hasWithdrawAuth) {
            errors.stakeAccAddr = new errors_2.SolanaStakeNoStakeAuth();
        }
        yield validateValidatorCommon(uiState.voteAccAddr, errors, api);
        if (!errors.voteAccAddr && stake !== undefined) {
            switch (stake.activation.state) {
                case "active":
                case "activating":
                    errors.stakeAccAddr = new errors_2.SolanaStakeAccountIsNotDelegatable();
                    break;
                case "inactive":
                    break;
                case "deactivating":
                    if (((_a = stake.delegation) === null || _a === void 0 ? void 0 : _a.voteAccAddr) !== uiState.voteAccAddr) {
                        errors.stakeAccAddr = new errors_2.SolanaStakeAccountValidatorIsUnchangeable();
                    }
                    break;
                default:
                    return (0, utils_1.assertUnreachable)(stake.activation.state);
            }
        }
        const txFee = yield (0, tx_fees_1.estimateTxFee)(api, mainAccount, "stake.delegate");
        if (mainAccount.spendableBalance.lt(txFee)) {
            errors.fee = new errors_1.NotEnoughBalance();
        }
        return {
            command: {
                kind: "stake.delegate",
                authorizedAccAddr: mainAccount.freshAddress,
                stakeAccAddr: uiState.stakeAccAddr,
                voteAccAddr: uiState.voteAccAddr,
            },
            fee: txFee,
            warnings: {},
            errors,
        };
    });
}
function deriveStakeUndelegateCommandDescriptor(mainAccount, model, api) {
    return __awaiter(this, void 0, void 0, function* () {
        const errors = {};
        const { uiState } = model;
        const stake = validateAndTryGetStakeAccount(mainAccount, uiState.stakeAccAddr, errors);
        if (stake !== undefined) {
            switch (stake.activation.state) {
                case "active":
                case "activating":
                    break;
                case "inactive":
                case "deactivating":
                    errors.stakeAccAddr = new errors_2.SolanaStakeAccountIsNotUndelegatable();
                    break;
                default:
                    return (0, utils_1.assertUnreachable)(stake.activation.state);
            }
            if (!errors.stakeAccAddr && !stake.hasStakeAuth && !stake.hasWithdrawAuth) {
                errors.stakeAccAddr = new errors_2.SolanaStakeNoStakeAuth();
            }
        }
        const txFee = yield (0, tx_fees_1.estimateTxFee)(api, mainAccount, "stake.undelegate");
        if (mainAccount.spendableBalance.lt(txFee)) {
            errors.fee = new errors_1.NotEnoughBalance();
        }
        return {
            command: {
                kind: "stake.undelegate",
                authorizedAccAddr: mainAccount.freshAddress,
                stakeAccAddr: uiState.stakeAccAddr,
            },
            fee: txFee,
            warnings: {},
            errors,
        };
    });
}
function deriveStakeWithdrawCommandDescriptor(mainAccount, tx, model, api) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const errors = {};
        const { uiState } = model;
        const stake = validateAndTryGetStakeAccount(mainAccount, uiState.stakeAccAddr, errors);
        if (!errors.stakeAccAddr && stake !== undefined) {
            if (!stake.hasWithdrawAuth) {
                errors.stakeAccAddr = new errors_2.SolanaStakeNoWithdrawAuth();
            }
            else if (stake.withdrawable <= 0) {
                errors.stakeAccAddr = new errors_2.SolanaStakeAccountNothingToWithdraw();
            }
        }
        const txFee = yield (0, tx_fees_1.estimateTxFee)(api, mainAccount, "stake.withdraw");
        if (mainAccount.spendableBalance.lt(txFee)) {
            errors.fee = new errors_1.NotEnoughBalance();
        }
        return {
            command: {
                kind: "stake.withdraw",
                authorizedAccAddr: mainAccount.freshAddress,
                stakeAccAddr: uiState.stakeAccAddr,
                amount: (_a = stake === null || stake === void 0 ? void 0 : stake.withdrawable) !== null && _a !== void 0 ? _a : 0,
                toAccAddr: mainAccount.freshAddress,
            },
            fee: txFee,
            warnings: {},
            errors,
        };
    });
}
function deriveStakeSplitCommandDescriptor(mainAccount, tx, model, api) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        const errors = {};
        // TODO: find stake account in the main acc when synced
        const { uiState } = model;
        // TODO: use all amount
        if (tx.amount.lte(0)) {
            errors.amount = new errors_1.AmountRequired();
        }
        // TODO: else if amount > stake balance
        if (!(0, logic_1.isValidBase58Address)(uiState.stakeAccAddr)) {
            errors.stakeAccAddr = new errors_1.InvalidAddress();
        }
        (_b = (_a = mainAccount.solanaResources) === null || _a === void 0 ? void 0 : _a.stakes) !== null && _b !== void 0 ? _b : [];
        const commandFees = yield (0, web3_1.getStakeAccountMinimumBalanceForRentExemption)(api);
        const splitStakeAccAddrSeed = `stake:${Math.random().toString()}`;
        const splitStakeAccAddr = yield (0, web3_1.getStakeAccountAddressWithSeed)({
            fromAddress: mainAccount.freshAddress,
            seed: splitStakeAccAddrSeed,
        });
        return {
            command: {
                kind: "stake.split",
                authorizedAccAddr: mainAccount.freshAddress,
                stakeAccAddr: uiState.stakeAccAddr,
                amount: tx.amount.toNumber(),
                seed: splitStakeAccAddrSeed,
                splitStakeAccAddr,
            },
            fee: commandFees,
            warnings: {},
            errors: {},
        };
    });
}
// if subaccountid present - it's a token transfer
function updateModelIfSubAccountIdPresent(tx) {
    if (tx.subAccountId) {
        return Object.assign(Object.assign({}, tx), { model: {
                kind: "token.transfer",
                uiState: Object.assign(Object.assign({}, tx.model.uiState), { subAccountId: tx.subAccountId }),
            } });
    }
    return tx;
}
function isAccountFunded(address, api) {
    return __awaiter(this, void 0, void 0, function* () {
        const balance = yield api.getBalance(address);
        return balance > 0;
    });
}
function validateRecipientCommon(mainAccount, tx, errors, warnings, api) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!tx.recipient) {
            errors.recipient = new errors_1.RecipientRequired();
        }
        else if (mainAccount.freshAddress === tx.recipient) {
            errors.recipient = new errors_1.InvalidAddressBecauseDestinationIsAlsoSource();
        }
        else if (!(0, logic_1.isValidBase58Address)(tx.recipient)) {
            errors.recipient = new errors_1.InvalidAddress();
        }
        else {
            const recipientWalletIsUnfunded = !(yield isAccountFunded(tx.recipient, api));
            if (recipientWalletIsUnfunded) {
                warnings.recipient = new errors_2.SolanaAccountNotFunded();
            }
            if (!(0, logic_1.isEd25519Address)(tx.recipient)) {
                warnings.recipientOffCurve = new errors_2.SolanaAddressOffEd25519();
            }
        }
    });
}
function validateMemoCommon(memo, errors) {
    const memoBytes = Buffer.from(memo, "utf-8");
    if (memoBytes.byteLength > logic_1.MAX_MEMO_LENGTH) {
        errors.memo = errors.memo = new errors_2.SolanaMemoIsTooLong(undefined, {
            maxLength: logic_1.MAX_MEMO_LENGTH,
        });
        // LLM expects <transaction> as error key to disable continue button
        errors.transaction = errors.memo;
    }
}
function validateValidatorCommon(addr, errors, api) {
    return __awaiter(this, void 0, void 0, function* () {
        if (addr.length === 0) {
            errors.voteAccAddr = new errors_2.SolanaValidatorRequired();
        }
        else if (!(0, logic_1.isValidBase58Address)(addr)) {
            errors.voteAccAddr = new errors_1.InvalidAddress();
        }
        else {
            const voteAcc = yield (0, web3_1.getMaybeVoteAccount)(addr, api);
            if (voteAcc instanceof Error || voteAcc === undefined) {
                errors.voteAccAddr = new errors_2.SolanaInvalidValidator();
            }
        }
    });
}
function validateAndTryGetStakeAccount(account, stakeAccAddr, errors) {
    var _a;
    if (stakeAccAddr.length === 0) {
        errors.stakeAccAddr = new errors_2.SolanaStakeAccountRequired();
    }
    else if (!(0, logic_1.isValidBase58Address)(stakeAccAddr)) {
        errors.stakeAccAddr = new errors_1.InvalidAddress();
    }
    if (!errors.stakeAccAddr) {
        const stake = (_a = account.solanaResources) === null || _a === void 0 ? void 0 : _a.stakes.find(stake => stake.stakeAccAddr === stakeAccAddr);
        if (stake === undefined) {
            errors.stakeAccAddr = new errors_2.SolanaStakeAccountNotFound();
        }
        return stake;
    }
    return undefined;
}
//# sourceMappingURL=js-prepareTransaction.js.map